import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {BehaviorSubject, Subscription} from 'rxjs';

import {loadModules} from 'esri-loader';

import {AGSLayer} from '../../models/AGSLayer';
import {AGSLayerList} from '../../models/AGSLayerList';
import {ConfigService} from '../config/config.service';
import {MapService} from '../map/map.service';
import {LayerService} from '../layer/layer.service';
import {GoogleAnalyticsService} from '../googleAnalytics/google-analytics.service';

@Injectable({
  providedIn: 'root'
})
export class TableOfContentsService {
  public themesMenuTrigger;
  private tocGroupProperties: {};
  private parentNodeArr: any[] = [];
  public nodeArray = [];
  public currentTheme = '';
  public layerElem: AGSLayer = null;
  private mapScale: number;
  public defaultVisibleLayers: AGSLayer[];
  public viewReference: any;
  public mapView: any;
  private mapObj: any;
  public mapExtent: any;
  public currLayer: any;  // this is a layer on the map
  public mapRef: any;
  public visibleLayersList: any[] = [];
  public parentElemsObj: any = {};
  public firstRunFlag = true;
  public graphicsLayerAddIndex = 0;
  public pointLayerAddIndex = 0;
  public lineLayerAddIndex = 0;
  public polygonLayerAddIndex = 0;
  public rasterLayerIndex = 0;
  public otherLayerIndex = 0;
  private tagList: any = [];

  // Observables
  public layerCheckBoxChanged$;
  public layerListUpdated$;
  public layerLoaded$;
  public layerLoadError$;
  public layerNodeArrUpdated$;
  public themeDataLoaded$;
  public layerServiceInfoComplete$;
  private layerCheckBoxChanged = new BehaviorSubject<any>(this.layerElem);
  private layerListUpdated = new BehaviorSubject<any>(this.visibleLayersList);
  public layerLoaded = new BehaviorSubject<any>(null);
  public layerLoadError = new BehaviorSubject<any>(null);
  private layerNodeArrUpdated = new BehaviorSubject<any>(this.configService.tocNodeArray);
  public themeDataLoaded = new BehaviorSubject<any>(null);
  private themeClickObs = new BehaviorSubject<any>(null);
  private themeChangeObs = new BehaviorSubject<any>(null);
  private layerServiceInfoComplete = new BehaviorSubject<any>(this.layerElem);
  public featuretableObservable = new BehaviorSubject(null);

  constructor(private http: HttpClient, private configService: ConfigService, private ga: GoogleAnalyticsService,
              private mapService: MapService,
              private layerService: LayerService, public agsLayerList: AGSLayerList) {

    this.defaultVisibleLayers = [];
    this.layerServiceInfoComplete$ = this.layerServiceInfoComplete.asObservable();
    this.layerListUpdated$ = this.layerListUpdated.asObservable();
    this.layerLoaded$ =  this.layerLoaded.asObservable();
    this.layerLoadError$ =  this.layerLoadError.asObservable();
    this.layerNodeArrUpdated$ = this.layerNodeArrUpdated.asObservable();
    this.themeDataLoaded$ = this.themeDataLoaded.asObservable();
    this.layerCheckBoxChanged$ = this.layerCheckBoxChanged.asObservable();
  }

  public getLayerCheckBoxChanged() { return this.layerCheckBoxChanged.asObservable(); }
  public getlayerServiceInfoComplete() { return this.layerServiceInfoComplete.asObservable(); }
  public getfeaturetableObservable() { return this.featuretableObservable.asObservable(); }
  public getThemeDataLoaded() { return this.themeDataLoaded.asObservable(); }
  public setTagList(tagList) { this.tagList = tagList; }
  public getTagList() { return this.tagList; }

  public getThemeClickObs() { return this.themeClickObs.asObservable(); }
  public getThemeChangeObs() { return this.themeChangeObs.asObservable(); }

  /**
   * Set current theme to url parameter if present, otherwise set current theme to initial theme from config.
   */
  public setupCurrentTheme(themeHash) {
    try {
      this.currentTheme = this.configService.CurrentTheme;
      console.log('Current Theme: ' + this.currentTheme);
      let hashRoute;
      if (window.location.href) {
        const url = window.location.href.split('#');

        if (url[1] && !(url[1].startsWith('access_token'))) {
          const hash = url[1].split('?');
          if (hash[0]) {
            hashRoute = hash[0];
          }
        }
      }

      if (!hashRoute) {
        this.currentTheme = this.configService.initialTheme;
      } else {
        if (this.configService.CurrentTheme) {
          this.currentTheme = this.configService.CurrentTheme;
        }
        else {
          this.currentTheme = this.configService.themeUrlParameters[hashRoute];
        }
      }
      this.configService.CurrentTheme = this.currentTheme;
      return this.currentTheme;
    } catch (error) {
      this.ga.exception(error, false);
      return this.configService.CurrentTheme;
    }
  }

  /**
   * Check for authorization against ArcGIS Portal if necessary
   * @param config: Config file to check against
   */
  public async getAuthorization(config) {
    try {
      let portalAuthComplete = false;
      const appAccessType = config.appAccessType;
      if (appAccessType && appAccessType.toLowerCase() === 'org') {
        const [
          OAuthInfo,
          IdentityManager
        ] = await loadModules([
          'esri/identity/OAuthInfo',
          'esri/identity/IdentityManager'
        ]);
        // Get Authorization
        const portalUrl = config.PortalUrl;
        const portalAppID = config.PortalAppID;
        const portalTokenUrl = config.PortalTokenUrl;

        if (portalUrl && portalAppID && portalTokenUrl) {
          const info = new OAuthInfo({
            appId: portalAppID,
            portalUrl,
            popup: false
          });

          config.portalAuthInfo = info;
          IdentityManager.registerOAuthInfos([info]);
          const portalCredential = await IdentityManager.checkSignInStatus;
          if (portalCredential && portalCredential.token) {
            config.portalCredential = portalCredential;
            config.portalToken = portalCredential.token;
            // console.log('portalToken it toc: ' + config.portalToken);
            portalAuthComplete = true;
          } else {
            const portalUser = await IdentityManager.getCredential(info.portalUrl + '/sharing');
            if (portalUser && portalUser.token){
              config.portalToken = portalUser.token;
              config.portalCredential = portalUser;
            }
            portalAuthComplete = !!portalUser;
          }
        }
      } else {
        portalAuthComplete = true;
      }
      return portalAuthComplete;
    } catch (error) {
      this.ga.exception(error, true);
    }
  }

  /**
   * Notify observers that theme has changed
   */
  public themeClick(newTheme) {
    let openTocNodes = document.querySelectorAll('.toc-indent:not(.toc-tree-invisible)');
    for(let i=0; i<openTocNodes.length; i++){
      openTocNodes[i].classList.add('toc-tree-invisible');
    }
    this.themeClickObs.next(newTheme);
    // setTimeout(() => {
    //   // Subscribers are receiving the last themeName observable as soon as they subscribe even
    //   //  if the theme Change already happened. So am sending a null value so new subscribers don't
    //   //  receive previous values. Probably a better way to do this.
    //   this.themeClickObs.next(null);
    // }, 1000);
  }

   /**
   * Notify observers that theme has changed
   */
  public themeChange(newTheme) {
    console.log('Theme change: ' + newTheme);
    this.themeChangeObs.next(newTheme);
  }


  /**
   * Gets theme TOC nodes, default visible layers
   * @param themeNodeArrFile: config filename for current Theme
   * @returns json nodeArr object for theme
   */
  public async getThemeNodeArr(themeNodeArrFile) {
    return await this.http.get(themeNodeArrFile).toPromise();
  }

  /**
   * Set tocGroupProperties dictionary with properties for TOC folders
   * @param TOCGroupPropertyArr: aary of toc group properties
   */
  public setTOCGroupProperties(TOCGroupPropertyArr: any) {
    const tocGroupProperties: {
      [index: string]: any; } = {};
    for (const tocGrpObj of TOCGroupPropertyArr) {
      for (const tocGrpObjKey in tocGrpObj) {
        if (tocGrpObj.hasOwnProperty(tocGrpObjKey)) {
          tocGroupProperties[tocGrpObjKey] = tocGrpObj[tocGrpObjKey];
        }
      }
    }
    this.tocGroupProperties = tocGroupProperties;
    return( this.tocGroupProperties);
  }

  /**
   * Responds to a layer checkbox clicked event
   * @param event: layer checkbox clicked event
   * @param layerElem: a layer element
   */
  public async layerCkBoxClicked(event: any, layerElem: AGSLayer) {
    this.layerElem = layerElem;
    // Set layers dashboard if applicable
    layerElem.layerDashboardInfo = this.getlayerDashboardInfo(layerElem);
    if (!this.mapView || !this.mapRef) {
      this.setMapView(this.mapService.getMapObject(), this.mapService.getViewRef());
    }
    await this.layerChecked(layerElem);
  }

  /**
   * Responds to a parent toc element clicked event
   * @param parentElem: parent TOC element
   */
  public parentCkBoxClicked(parentElem: any) {
    this.parentChecked(parentElem);
  }

  /**
   * Responds to a mapscale changed event
   */
  public mapScaleChangedEvt() {
    this.mapScale = this.mapService.getMapScale();
    this.areMapLayersInScale(this.mapScale);
  }

  /**
   * Responds to a mapextent changed event
   */
  public mapExtentChangedEvt() {
    this.mapExtent = this.mapService.getMapExtent();
  }

  /**
   * Returns the default visible layers for the current theme
   */
  public getDefaultVisibleLayers() {
    // Get default visible layers array
    if (this.configService && this.configService.defaultVisibleLayers && this.configService.CurrentTheme) {
      return this.configService.defaultVisibleLayers[this.configService.CurrentTheme];
    } else {
      return null;
    }
  }

  /**
   * Sets the mapview, map object, viewReference and current mapscale and mapextent
   * @param mapObj: map object
   * @param viewObj: view object
   */
  public setMapView(mapObj: any, viewObj: any) {
    // Set the mapview reference
    this.mapView = viewObj;
    this.mapObj = mapObj;
    this.viewReference = viewObj;
    this.mapScale = this.mapService.getMapScale();
    this.mapExtent = this.mapService.getMapExtent();
  }

  public getMapScale() {
      return this.mapService.getMapScale();
  }

  /**
   * Load the default visible layers on the map
   */
  public showDefaultVisibleLayers() {
    // Show defaultVisible Layers on map.
    const hashRoute = window.location.hash.split('?');
    let viewObjs: Subscription = new Subscription();
    let mapRef1: Subscription = new Subscription();
    //if (!hashRoute[2] || !(hashRoute[2].length > 0)) {
      mapRef1 = this.mapService.getmapLoadedObservable().subscribe( (mapValue1) => {
        if (mapValue1) {
          viewObjs = this.mapService.getViewRefObservable().subscribe( async (mapValue)  => {
            if (mapValue) {
              this.defaultVisibleLayers = this.getDefaultVisibleLayers();
              if (this.defaultVisibleLayers && Array.isArray(this.defaultVisibleLayers) &&
                this.defaultVisibleLayers.length > 0) {
                for (const defVisLayer of this.defaultVisibleLayers) {
                  // Create an event object with a checked property to pass to the function
                  const event: any = {};
                  event.currentTarget = {};
                  event.currentTarget.checked = true;
                  this.setMapView(this.mapService.getMapObject(), this.mapService.getViewRef());
                  this.layerCkBoxClicked(event, defVisLayer);
                  if (viewObjs !== undefined) {
                    viewObjs.unsubscribe();
                  }
                  if (mapRef1) {
                    mapRef1.unsubscribe();
                  }
                }
              }
            }
          });
        }
      });

  }

  /**
   * Returns the layers currently on the map
   */
  public getLayersOnMap() {
    this.mapRef = this.mapView.map;
    const layerViews = this.mapRef.layers;
    if (layerViews !== undefined && layerViews.items !== undefined) {
      return layerViews.items;
    } else {
      return null;
    }
  }

  /**
   * Checks each layer on map to see if its in scale
   * @param mapScale: current mapscale
   */
  public areMapLayersInScale(mapScale: any) {
    const layerItems = this.getLayersOnMap();
    if (layerItems !== undefined) {
      for (const mapLayerObj of layerItems) {
        this.isLayerInScale(mapScale, mapLayerObj);
      }
    } else {
      this.ga.exception('layerViews undefined', false);
    }
  }

  /**
   * Returns the current mapextent
   */
  public getMapExtent() {
    return this.mapView.extent; // mapRef.extent;
  }

  /**
   *  Return whether currLayer is in scale
   * @param mapScale: current mapscale
   * @param mapLayerObj: map object
   */
  public isLayerInScale(mapScale: any, mapLayerObj: any) {
    // Note: mapLayerObj is a layer (items) from the mapView not an AGSLayer object.
    let isInScale: boolean;
    // if passed in mapscale parameter is null then use the mapView.scale property if available
    if (mapScale === undefined) {
      mapScale = this.getMapScale();
    }

    if (this.currLayer) {
      if (mapLayerObj && mapScale) {
        const serviceLayerID = mapLayerObj.id;
        isInScale = false;
        const currLayer = this.agsLayerList.findLayerInVisibleLayerListByID(serviceLayerID);
        const subLayers = mapLayerObj.allSublayers;
        if (currLayer ) {
          // Change subLayers to subLayers.items in 4.1
          if (subLayers && subLayers.items ) {
            // Change subLayers to subLayers.items in 4.1
            const subLayerItems = subLayers.items;
            for (const subLayer of subLayerItems) {
              // Check minscale/maxscale properties
              // Check all sublayers but skip groupLayers (subLayer.sublayers)
              if (subLayer.visible && subLayer.sublayers === undefined) {
                // ** Todo: Need to check currLayer minscale/maxscale also
                isInScale = (subLayer.minScale === 0 || subLayer.minScale === undefined
                  || subLayer.minScale >= mapScale)
                  && (subLayer.maxScale === 0 || subLayer.maxScale === undefined
                    || subLayer.maxScale <= mapScale) &&
                  (currLayer.minScale === 0 || currLayer.minScale === undefined
                    || currLayer.minScale >= mapScale)
                  && (currLayer.maxScale === 0 || currLayer.maxScale === undefined
                    || currLayer.maxScale <= mapScale);
                if (isInScale  && currLayer) {
                  // if any one of the subLayers are  visible and in scale, the layer is in scale
                  currLayer.isInScale = true;
                  break;
                }

              } // end sublayer.visible //
            } // end for
            currLayer.isInScale = isInScale;

          } else {
            // feature layers and graphic layers may not have sublayers
            if (mapLayerObj.minScale !== undefined  && mapLayerObj.maxScale !== undefined) {
              isInScale = (mapLayerObj.minScale === 0 || mapLayerObj.minScale
                === undefined || mapLayerObj.minScale >= mapScale)
                && (mapLayerObj.maxScale === 0 || mapLayerObj.maxScale === undefined
                  || mapLayerObj.maxScale <= mapScale);
              currLayer.isInScale = isInScale;
            }
          }
        }
      }
      return isInScale;
    }
  }

  /**
   * Returns the map object for the input layer
   * @param mapRef: map reference
   * @param serviceLayerID: serviceLayerID
   */
  public getLayerFromView(mapRef: any, serviceLayerID: string) {
    let mapLayerObj: any;
    mapRef = this.mapView.map;
    const layerViews = mapRef.layers;
    if (layerViews !== undefined) {
      const layerItems = layerViews.items;
      for (const layerItem of layerItems) {
        if (layerItem.id === serviceLayerID) {
          mapLayerObj = layerItem;
          break;
        }
      }
    }
    return mapLayerObj;
  }

  /**
   * Add or toggle visibility of input layer
   * @param layerElem: Input layer element
   */

  public async layerChecked(layerElem: AGSLayer) {
    try {
      // Layer checked in TOC so add or toggle visibility of Layer
      if (layerElem === undefined ) {
        console.log('layerElem undefined in layerChecked');
        return;
      }

      // Test to see if layerElem has a mapLayer object created yet, if not create it
      if (layerElem && !(layerElem.mapImageLayer || layerElem.featureLayer || layerElem.imageryLayer ||
        layerElem.graphicsLayer || layerElem.csvLayer || layerElem.wmsLayer)) {
        // The preloaded tocNodeArray contains AGSLayer objects without the functions. This will add the functions to the object.
        layerElem = await new AGSLayer(layerElem);
        if (layerElem.needsToken && this.configService.portalToken) {
          layerElem.token = this.configService.portalToken;
          if (layerElem.relateObj && layerElem.relateObj.length > 0) {
            // Add tokens to related table for this secure service
            layerElem.relateObj.forEach( (relatedTable) => {
              relatedTable['token'] = layerElem.token;
            });
          }
        }


        await layerElem.getMapLayer();  // Get the map object that can be added to the mapview
      }

      if (!this.mapView || !this.mapObj) {
        this.setMapView(this.mapService.getMapObject(), this.mapService.getViewRef());
      }

      if (this.mapView === undefined || this.mapView.map === undefined) {
        console.log('in tocservice layerChecked: mapView is undefined');
      } else {
        this.mapRef = this.mapView.map;
        if (layerElem.serviceLayerID !== undefined) {
          this.currLayer = this.mapRef.findLayerById(layerElem.serviceLayerID);
          if (this.currLayer === undefined) {
            // Get the info for this layer from the rest endpoint and update the layer element.
            const retLayerElem: any = await this.getLayerServiceInfo(layerElem);
            if (retLayerElem) {
              let mapObj: any ;
              if (!layerElem.minScale && retLayerElem.minScale) {
                layerElem.minScale = retLayerElem.minScale;
              }
              if (!layerElem.maxScale && retLayerElem.maxScale) {
                layerElem.maxScale = retLayerElem.maxScale;
              }
              layerElem.isInScale = layerElem.isLayerInScale(this.getMapScale());
              if (layerElem.layerType === 'feature') {
                mapObj = layerElem.featureLayer;
              } else {
                  if (layerElem.layerType.toLowerCase().includes('arcgisimageservicelayer') ||
                  layerElem.layerType.toLowerCase().includes('imagerylayer')) {
                    mapObj = layerElem.imageryLayer;
                  }
                  else {
                    mapObj = layerElem.mapImageLayer;
                  }
              }
              if (layerElem.transparency) {
                  mapObj.opacity = layerElem.transparency;
              }
              if (layerElem.labelsVisible != undefined && layerElem.labelsVisible === false) {
                  // Don't turn labels on by default, let user turn on labels when applicable
                  //console.log('set labelsVisibe false for: ' + layerElem.id + ' labelsVisible: ' + layerElem.labelsVisible);
                  mapObj.labelsVisible = false;
              }

              // Add layer to map
              await this.addLayerToMap(mapObj, retLayerElem);
              // Add updated layerElem to dictionary
              if (retLayerElem.layerAvailable) {
                if (retLayerElem.isChecked) {
                  this.agsLayerList.addLayerToVisibleLayerList(layerElem);
                  this.updateLayersList();
                }
                // Fire layerCheckBoxChanged event for those components subscribed to this.
                this.layerCheckBoxChanged.next(layerElem);
              }
            }
          } else {
            // toggle layer visiblity
            if (layerElem.isChecked) {
              if (layerElem.isParentChecked === undefined || layerElem.isParentChecked) {
                this.currLayer.visible = layerElem.isChecked;
                layerElem.isVisible = layerElem.isChecked;
                this.agsLayerList.addLayerToVisibleLayerList(layerElem);
                this.updateLayersList();
                const mapLayerObj: any = this.mapService.getLayerFromMapView(this.mapRef, layerElem.serviceLayerID);
                if (this.isLayerInScale(this.getMapScale(), mapLayerObj)) {
                  mapLayerObj.visible = true;
                }
              } else {
                // Parent folder not checked
                this.currLayer.visible = false;
                layerElem.isVisible = false;
              }
            } else {
              // turn layer off
              this.currLayer.visible = layerElem.isChecked;
              layerElem.isVisible = layerElem.isChecked;
            }
            // Fire layerCheckBoxChanged event for those components subscribed to this.
            this.layerCheckBoxChanged.next(layerElem);
          }

        } else {
          this.ga.exception('in 2-d-view layerCk: no serviceLayerID for: ' + layerElem.name, false);
        }
      }
      if (this.viewReference === undefined ) {
        this.ga.exception('in 2-d-view layerck viewReference is undefined', false);
      }
      if (!layerElem.layerAvailable) {
       return (layerElem.layerAvailable);
      }
    } catch (error) {
      this.ga.exception(error, false);
    }
  }



  /**
   * Add layer to map
   * @param mapLayer: map layer
   * @param layerElem: layer
   * @param mapIndex: optional mapIndex to add the layer to
   */
  public async addLayerToMap(mapLayer, layerElem: AGSLayer, mapIndex?: number) {
    try {
      // Test to see if layerElem has a mapLayer object created yet, if not create it
      if (layerElem && !(layerElem.mapImageLayer || layerElem.featureLayer || layerElem.imageryLayer ||
        layerElem.graphicsLayer || layerElem.csvLayer || layerElem.wmsLayer)) {
        // The preloaded tocNodeArray contains AGSLayer objects without the functions. This will add the functions to the object.
        layerElem = new AGSLayer(layerElem);
        await layerElem.getMapLayer();  // Get the map object that can be added to the mapview
      }

      // const loadingDiv = document.getElementById('loading-svg');
      // loadingDiv.style.visibility = 'unset';

      if (!this.mapView || !this.mapRef) {
        this.setMapView(this.mapService.getMapObject(), this.mapService.getViewRef());
      }
      // Get map reference
      this.mapRef = this.mapView.map;
      // Check to see if layer is already on map, if so return
      this.currLayer = this.mapRef.findLayerById(layerElem.serviceLayerID);
      if (this.currLayer) {
        return;
      }

      // Get index on map to add layer
      if (!mapIndex) {
        mapIndex = this.getMapIndex(layerElem);
      }

      // Dispatch layer loading if layer will be visible on map
      if (layerElem.isParentChecked === undefined || layerElem.isParentChecked
        && layerElem.isLayerInScale && layerElem.isLayerInScale(this.getMapScale())){
      }

      // Add layer to map
      this.mapView.map.add(mapLayer, mapIndex);

      // When layer loaded is complete
      const res = this.viewReference.whenLayerView(mapLayer).then((layerView: any) => {
        const mapLayerObj: any = layerView.layer;
        console.log('Layer loaded to map: ' + mapLayer.id);
        this.layerLoaded.next(mapLayerObj);
        if (!layerElem.isChecked || (layerElem.isParentChecked !== undefined &&
          !layerElem.isParentChecked)) {
          mapLayerObj.visible = false;
        } else {
          if (this.isLayerInScale(this.getMapScale(), mapLayerObj)) {
            mapLayerObj.visible = true;
          }
        }
        if (layerElem.layerType === 'wms') {
          // Update name for wms layers
          const wmsLayer = layerElem.wmsLayer;
          layerElem.name = wmsLayer.title;
        }
        if (mapLayerObj.objectIdField && mapLayerObj.objectIdField.length > 0) {
          layerElem.objectIdField = mapLayerObj.objectIdField;
        }

        // Get layer's full extent
        layerElem.fullExtent = mapLayerObj.fullExtent;
        if (this.mapRef.layers !== undefined && this.mapRef.layers.items !== undefined) {
          // Get layer order index for the this layer on map
          const arrInd = this.mapRef.layers.items.indexOf(mapLayerObj);
          if (arrInd > -1) {
            layerElem.layerOrder = (this.mapRef.layers.items.length - 1) - arrInd;
            layerElem.id = mapLayerObj.id;
            layerElem.serviceLayerID = mapLayerObj.id;
            this.agsLayerList.addLayerToVisibleLayerList(layerElem);
            this.updateLayersList();
          }
        }
        // loadingDiv.style.visibility = 'hidden';
      }, (error) => {
        layerElem.layerAvailable = false;
        console.log('Error in loading layer: ' + layerElem.id + ' Error: ' + error);
        this.layerLoadError.next(layerElem);
      });

    } catch (error) {
      layerElem.layerAvailable = false;
      console.log('Error in loading layer: ' + layerElem.id + ' Error: ' + error);
      this.layerLoadError.next(layerElem);
      this.ga.exception(error, false);
    }
  }

  /**
   * Add layer to node array for TOC
   * @param layerElem: layer element
   */
  public addLayerToNodeArr(layerElem) {
    // Find parent folder
    if (this.configService.tocNodeArray) {
      for (const currentNode of this.configService.tocNodeArray) {
        if (currentNode.name === layerElem.parentName) {
          const parentNodeArr: any = currentNode.nodes;
          if (parentNodeArr) {
            for (const childNode of this.parentNodeArr) {
              if (childNode.serviceLayerID === layerElem.serviceLayerID) {
                // This layer already exists in TOC
                console.log('layer already exists in TOC: ' + layerElem.serviceLayerID);
                return false;
              }
            }
            // Add this layer to parent in tocNodeArray
            parentNodeArr.push(layerElem);
            this.layerNodeArrUpdated.next(this.configService.tocNodeArray);
            return true;
          }
        }
      }
    } else {
      console.log('no nodearr');
    }
    return false;
  }


  /**
   * Get index of layer on map
   * @param layerElem: layer
   */
  public async getLayerServiceInfo(layerElem:AGSLayer) {
    // Get the info from the layer service for this layer from the rest endpoint and update the layer element.
    const retLayerElem = await this.layerService.getLayerServiceInfo(layerElem);
    this.layerServiceInfoComplete.next(retLayerElem);
    return retLayerElem;
  }

   /**
   * serviceUrl:string
   * @param : layer
   */
  public async getServiceInfo(serviceUrl:string) {
    // Get the info from the serviceUrl from the rest endpoint .
    return await this.layerService.getServiceInfo(serviceUrl);
  }

  /**
   * Get index of layer on map
   * @param layerElem: layer
   */
  public getMapIndex(layerElem: AGSLayer) {
    // Get index to add layer to map
    let mapIndex;
    let layerGeomType = layerElem.geometryType;
    const layerType = layerElem.type;
    if (! layerGeomType && layerType && layerType.toLowerCase() === 'raster layer') {
      layerGeomType = 'raster';
    }
    // Set default if no geometry type
    if (! layerGeomType ) {
      layerGeomType = 'other';
    }
    // Set map index
    switch (layerGeomType) {
      case 'graphics':
        mapIndex = this.graphicsLayerAddIndex;
        this.graphicsLayerAddIndex++;
        break;
      case 'esriGeometryPoint':
        mapIndex = this.pointLayerAddIndex;
        this.pointLayerAddIndex++;
        this.graphicsLayerAddIndex++;
        break;
      case 'esriGeometryPolyline':
        mapIndex = this.lineLayerAddIndex;
        this.lineLayerAddIndex++;
        this.pointLayerAddIndex++;
        this.graphicsLayerAddIndex++;
        break;
      case 'esriGeometryPolygon':
        mapIndex = this.polygonLayerAddIndex;
        this.polygonLayerAddIndex++;
        this.lineLayerAddIndex++;
        this.pointLayerAddIndex++;
        this.graphicsLayerAddIndex++;
        break;
      case 'raster':
        mapIndex = this.rasterLayerIndex;
        this.rasterLayerIndex++;
        this.polygonLayerAddIndex++;
        this.lineLayerAddIndex++;
        this.pointLayerAddIndex++;
        this.graphicsLayerAddIndex++;
        break;
      default:
        mapIndex = this.otherLayerIndex;
        this.otherLayerIndex++;
        this.rasterLayerIndex++;
        this.polygonLayerAddIndex++;
        this.lineLayerAddIndex++;
        this.pointLayerAddIndex++;
        this.graphicsLayerAddIndex++;
        break;
    }

    return mapIndex;
  }

  /**
   * Add feature layer to map
   * @param mapLayerObj: map layer object
   */
  public addFeatLayerToMap(mapLayerObj) {
    // Add layer to map
    // ** todo: Check if layer already exists
    // ** todo deal with mapindex here
    this.mapRef.add(mapLayerObj, 1);
    this.viewReference.whenLayerView(mapLayerObj).then((layerView: any) => {
      mapLayerObj = layerView.layer;
    });
  }

  /**
   * Update layers list
   */
  public updateLayersList() {
    this.visibleLayersList = this.agsLayerList.updateLayersList(this.mapRef.layers.items);
    // Fire layerListUpdated event for those components subscribed to this.
    this.layerListUpdated.next(this.visibleLayersList);
    return this.visibleLayersList;
  }

  /**
   * Update parent folder checked property
   * @param isChecked: isChecked status
   * @param parentFolder: parent folder
   */
  public updateParentNodes(isChecked: boolean, parentFolder: any) {
    if (parentFolder !== undefined) {
      parentFolder.checked = isChecked;
      parentFolder.isChecked = isChecked;
      if (parentFolder.nodes !== undefined) {
        const parentNodes = parentFolder.nodes;
        for (const parentNodeProp of parentNodes) {
          parentNodeProp.isParentChecked = parentFolder.checked;
          if (parentNodeProp.nodes) {
            // if subNodes set the isParentChecked property
            const subNodes = parentNodeProp.nodes;
            for (const subNodeItem of subNodes) {
              if ((parentNodeProp.isChecked || (! parentNodeProp.hasCheckBox)) && parentFolder.checked) {
                subNodeItem.isParentChecked = true;
                parentNodeProp.isChecked = true;
              } else {
                subNodeItem.isParentChecked = false;
                parentNodeProp.isChecked = false;
              }
            }
          }
        }
      }
      this.parentCkBoxClicked(parentFolder);
    }
  }

  /**
   * Parent checked on or off so turn on/off layer visibility
   * @param parentNode: parent node
   */
  public parentChecked(parentNode: any) {
    // Parent Node checked on or off in TOC so turn on or off visibility of Layer's that are checked in TOC
    if (parentNode === undefined || parentNode.nodes === undefined) {
      return null;
    }
    if (!this.mapView || !this.mapObj) {
      this.setMapView(this.mapService.getMapObject(), this.mapService.getViewRef());
    }
    let isParentChecked = parentNode.isChecked;
    if (!parentNode.hasCheckBox) {
      isParentChecked =  true; // If parent doesnt have a checkbox than parent is on by default
    }

    if (this.mapView === undefined || this.mapView.map === undefined) {
      this.ga.exception('in tocService:  mapView is undefined', false);
    } else {
      const mapRef = this.mapView.map;
      for (const parentNodeElem of parentNode.nodes) {
        const layerElem = parentNodeElem;
        layerElem.isParentChecked = isParentChecked;
        // Check layers that are currently checked in the checkbox
        if (layerElem.serviceLayerID !== undefined && layerElem.isChecked) {
          this.currLayer = mapRef.findLayerById(layerElem.serviceLayerID);
          if (this.currLayer !== undefined) {
            if (isParentChecked) {
              this.currLayer.visible = true;
              layerElem.isVisible = true;
            } else {
              this.currLayer.visible = false;
              layerElem.isVisible = false;
            }
            // Fire layerCheckBoxChanged event for those components subscribed to this.
            this.layerCheckBoxChanged.next(layerElem);
          } else {
            if (isParentChecked) {
              // Add layer to map if layer is checked
              // but not on the map yet because the parent was not checked
              // ** todo: check for parents parents ?
              this.layerChecked(layerElem);
            }

          }
        } // end if (layerElem != undefined && layerElem.serviceLayerID

        // Check for sub parents
        if (layerElem.nodes) {
          // if subNodes set the isParentChecked property
          const subNodes = layerElem.nodes;
          for (const subLayerElem of subNodes) {
            subLayerElem.isParentChecked = isParentChecked;
            if (subLayerElem.serviceLayerID !== undefined && subLayerElem.isChecked) {
              this.currLayer = mapRef.findLayerById(subLayerElem.serviceLayerID);
              if (this.currLayer !== undefined) {
                if (subLayerElem.isChecked && subLayerElem.isParentChecked) {
                  subLayerElem.isVisible = true;
                  this.currLayer.visible = true;
                } else {
                  subLayerElem.isVisible = false;
                  this.currLayer.visible = false;
                }

                this.layerCheckBoxChanged.next(subLayerElem);
              } else {
                if (isParentChecked) {
                  this.layerChecked(subLayerElem);
                }
              }
            }
          }
        }
      }
    }
  }

  /**
   * Move layer to input position on map
   * @param mapElem: map element
   * @param position: position on map
   */
  public moveLayerOnMap(mapElem: any, position: number) {
    // Reorder layer on map
    if (this.mapView.map.layers !== undefined && mapElem !== undefined) {
      this.mapView.map.layers.reorder(mapElem, position);
      this.viewReference.whenLayerView(mapElem).then(() => {
        this.updateLayersList();
      });
    }
  }

  /**
   * Turn off all layers on map
   */
  public turnOffAllLayers() {
    const visLayers: any = this.getVisibleLayerList();
    for (const layerElem of visLayers) {
      if (layerElem) {
        layerElem.isChecked = false;
      }
      this.layerChecked(layerElem);
    }
  }

  /**
   * Get list of visible layers
   */
  public getVisibleLayerList() {
    return this.agsLayerList.getVisibleLayerList();
  }

  /**
   * Get mapextent as a string
   */
  public getMapExtentString() {
    const mapExt = this.getMapExtent();
    return mapExt.xmin.toString() + ',' + mapExt.ymin.toString() + ',' + mapExt.xmax.toString() + ',' + mapExt.ymax.toString();
  }

   /**
   * Get mapextent as a string with the coordinates returned in the order of coordOrderArr
   * param: coordOrderArr:Array of the order that extent coordinates will be returned in string
   *  (ie ['xmin','xmax','ymin','ymax']
   */
  public getMapExtentCoordString(coordOrderArr) {
    const mapExt = this.getMapExtent();
    let mapExtStr = mapExt[coordOrderArr[0]].toString() + ',' + mapExt[coordOrderArr[1]].toString() + ','
    + mapExt[coordOrderArr[2]].toString() + ',' + mapExt[coordOrderArr[3]].toString();
    return mapExtStr;
  }

  /**
   * Populate object of parent elements and one of layer elements.
   * Also, populate arrays for parent and leyer elements
   * whose checkboxes are checked.
   */
  public getNodeElems(nodeArray, parentElemsObj, layerElemObjs, parentElemCheckedArr, layerElemCheckedArr) {
    for (const nodeArrElem of nodeArray) {
      if (nodeArrElem.nodes) {
        parentElemsObj[nodeArrElem.id] = nodeArrElem;
        if (parentElemsObj[nodeArrElem.id].isChecked
          && parentElemsObj[nodeArrElem.id].hasCheckBox) {
          parentElemCheckedArr.push(nodeArrElem.id) ;
        }
        this.getNodeElems(nodeArrElem.nodes, parentElemsObj,
          layerElemObjs, parentElemCheckedArr, layerElemCheckedArr);
      } else  {
        layerElemObjs[nodeArrElem.id] = nodeArrElem;
        if (layerElemObjs[nodeArrElem.id].isChecked) {
          layerElemCheckedArr.push(nodeArrElem.id) ;
        }
      }
    }
  }

  /**
   * Set the parent checkbox to the input isChecked status
   * @param parentFolderID: parent folder ID
   * @param isChecked: isChecked status of parent folder
   */
  public setParentCheckBox(parentFolderID, isChecked) {
    if (this.parentElemsObj && this.parentElemsObj[parentFolderID]) {
      const parentObj = this.parentElemsObj[parentFolderID];
      if (!parentObj.name) {
        parentObj.name = parentFolderID;
      }
      if (!parentObj.id) {
        parentObj.id = parentFolderID;
      }
      this.updateParentNodes(isChecked, parentObj);
    } else {
      this.ga.exception('Parent does not exist: ' + parentFolderID, false);
    }

  }

  /**
   * Get the parent folder object
   * @param parentFolderID: parent folder ID
   */
  public getParentFolder(parentFolderID) {
    return this.parentElemsObj[parentFolderID];
  }

  /**
   * Generate object keys
   * @param obj : object
   */
  public generateArrayKeys(obj: any) {
    return Object.keys(obj).map((key) =>  key );
  }

  /**
   * Get input theme
   * @param item:theme name
   */
  public getTheme(item) {
    if (item) {
      let chosenThemeName = decodeURIComponent(item);
      if (chosenThemeName && this.configService) {
        this.configService.CurrentTheme = chosenThemeName;
        if (this.configService.themeUrlParameters) {
          const urlThemeParameter =
            this.findKey(this.configService.themeUrlParameters, chosenThemeName);
          if (urlThemeParameter) {
            chosenThemeName = urlThemeParameter;
          }
        }
        return chosenThemeName;
      }
    }
  }

  /**
   * Find input key in object
   * @param dataObj: object
   * @param value: key
   */
  private findKey(dataObj, value) {
    for (const key in dataObj) {
      if (dataObj.hasOwnProperty(key) && dataObj[key] === value) {
        return key;
      }
    }
    return false;
  }

  /**
   * Change layer's transparency
   * @param layerElem: layer element
   * @param transparency: tranparency setting
   */
  public changeLayerTransparency(layerElem: AGSLayer, transparency: number) {
    // gets the layer from the map object
    const mapLayerObj = this.getLayerFromView(this.mapRef, layerElem.serviceLayerID);
    // sets the transparency on the AGSlayer object
    if (layerElem) {
      layerElem.transparency = transparency;
    }
    // sets the transparency on the layer from the map object
    if (mapLayerObj && mapLayerObj.opacity >= 0) {
      mapLayerObj.opacity = transparency;
    }
  }

  /**
   * Load a new basemap on map
   * @param basemapId
   */
  public async setBasemap(basemapId, isVisible) {
    const viewRef = this.mapService.getViewRef();
    if (viewRef && viewRef.map) {
      viewRef.map.basemap = basemapId;
      viewRef.map.basemap.visible = isVisible;
      // Set visibility of basemap in case it was previously toggled off or on
      if (viewRef.map.basemap.baseLayers && viewRef.map.basemap.baseLayers.items &&
        viewRef.map.basemap.baseLayers.items[0]) {
        viewRef.map.basemap.baseLayers.items[0].visible = isVisible;
      }
    }
    else {
      console.log('In setBasemap: Unable to load basemap ViewRef or viewRef.map doesnt exist');
    }
  }

  /**
   * Change basemap transparency
   * @param transparency: tranparency setting
   */
  public changeBasemapTransparency(transparency: number) {
    // Set opacity on basemap
    this.mapRef = this.mapService.getViewRef();
    if (this.mapRef && this.mapRef.map && this.mapRef.map.basemap && this.mapRef.map.basemap.baseLayers &&
      this.mapRef.map.basemap.baseLayers.items && this.mapRef.map.basemap.baseLayers.items[0]) {
        this.mapRef.map.basemap.baseLayers.items[0].opacity = transparency;
      }
  }

   /**
   * Toggle layer labels on or off
   * @param layerElem: layer element
   * @param transparency: tranparency setting
   */
  public toggleLayerLabels (layerElem: AGSLayer) {
    const mapLayerObj = this.getLayerFromView(this.mapRef, layerElem.serviceLayerID);
    if (mapLayerObj) {
      if (mapLayerObj.allSublayers && mapLayerObj.allSublayers.items) {
        for (const layer of mapLayerObj.allSublayers.items) {
          layer.labelsVisible = !layer.labelsVisible;
        }
      }
      mapLayerObj.labelsVisible = !mapLayerObj.labelsVisible;
    }
  }

  /**
   * Removes all layers from the map object for ToC reset
   */
  public removeAllLayers(exceptGraphics?: boolean) {
    const map = this.mapService.getMapObject();
    let layersToRemove = [];
    if (exceptGraphics) {
      map.layers.forEach((layer) => {
        if (layer.type !== 'graphics') {
          layersToRemove.push(layer);
        }
      })
      map.removeMany(layersToRemove);
    } else {
      map.removeAll();
    }
  }

  /**
   * Finds objects by key
   * @param obj
   * @param keyToFind
   * @param valueToFind
   * @returns
   */
  public findObjectsByKey(obj, keyToFind, valueToFind) {
    return Object.entries(obj)
      .reduce((acc, [key, value]) => (key === keyToFind && value === valueToFind)
        ? acc.concat(obj)
        : (typeof value === 'object')
        ? acc.concat(this.findObjectsByKey(value, keyToFind, valueToFind))
        : acc
      , []) || [];
  }

  /**
   * Getlayers dashboard info if applicable
   * @param layerElem
   * @returns
   */
  public getlayerDashboardInfo(layerElem) {
    if (layerElem.layerDashboardName && this.configService.dashboardInfo &&
      this.configService.dashboardInfo[layerElem.layerDashboardName]) {
      return this.configService.dashboardInfo[layerElem.layerDashboardName];
    }
    else {
      return null;
    }
  }


 /*  public getBasemapMetadata(newBasemap) {
    if (newBasemap && this.basemapNodes && this.basemapNodes[0]) {
      this.basemapNodes[0].id = newBasemap["title"];
      this.basemapNodes[0].name = newBasemap["title"];
      this.basemapNodes[0].nodeType = 'basemap';
      this.basemapNodes[0].description = null;
      // Set description from portalItem if exists
      if (newBasemap['portalItem'] && newBasemap['portalItem']['description'] &&
        newBasemap['portalItem']['description'].length > 0) {
        this.basemapNodes[0].description = newBasemap['portalItem']['description'];
      }
      this.basemapNodes[0].url = null;
      if (newBasemap['baseLayers'] && newBasemap['baseLayers'].items && newBasemap['baseLayers'].items[0]) {
        this.basemapNodes[0].baseLayer = newBasemap['baseLayers'].items[0];
        if (this.basemapNodes[0].baseLayer.sourceJSON && this.basemapNodes[0].baseLayer.sourceJSON.description) {
          // Set description from sourceJSON
          this.basemapNodes[0].description = this.basemapNodes[0].baseLayer.sourceJSON.description;
        }
        if (this.basemapNodes[0].baseLayer.url && this.basemapNodes[0].baseLayer.url.length > 0)  {
          this.basemapNodes[0].url = this.basemapNodes[0].baseLayer.url;
        }
        if (this.basemapNodes[0].baseLayer.opacity)  {
          this.basemapNodes[0].opacity = this.basemapNodes[0].baseLayer.opacity;
          this.basemapNodes[0].transparency = this.basemapNodes[0].baseLayer.opacity;
        }
      }
    }
  } */

}
